import type {
	AggressionProfile,
	MonetizationSettings,
	Segment,
} from "./settings";

/**
 * Maps a viewer + their session engagement onto an aggression segment.
 *
 * Tier gates DISPLAY aggression; depth ramps it so new/shallow visitors are not
 * blasted (activation is fragile). Pro short-circuits to the clean profile.
 * `returning` counts as deep — a repeat visitor has shown intent.
 */

export type Tier = "anon" | "free" | "pro";

export type SegmentInput = {
	tier: Tier;
	/** Pages / monetizable units seen this session. */
	sessionDepth: number;
	/** Repeat visitor (prior session). Treated as deep engagement. */
	returning?: boolean;
	/**
	 * The SHALLOW profile this tier starts in, when the caller has the resolved
	 * config to hand. Supplying it clamps the promotion threshold to what that
	 * profile can actually let a viewer reach — see
	 * {@link deepDepthThresholdFor}. Omit it and the flat
	 * {@link DEEP_DEPTH_THRESHOLD} applies, which is what every caller did before
	 * the deadlock below was found.
	 */
	shallowProfile?: AggressionProfile;
};

/** At/over this many session units a non-pro viewer is "deep". */
export const DEEP_DEPTH_THRESHOLD = 3;

/**
 * The depth at which a viewer in `shallow` is promoted to its deep counterpart.
 *
 * Session depth is `counts.display + counts.affiliate`, and it only ever grows
 * when a unit is actually SHOWN — so the shallow profile's own caps bound how
 * deep a viewer can ever get. When that bound is below `DEEP_DEPTH_THRESHOLD`
 * the deep segment is unreachable and its whole profile is dead configuration.
 *
 * That was not hypothetical (2026-09-03). The live config had `anon_shallow` at
 * `maxDisplay: 0` / `maxAffiliate: 2` / `sessionImpressionCap: 3` against a
 * threshold of 3: an anonymous visitor topped out at depth 2, could never become
 * `anon_deep`, and therefore sat on `maxDisplay: 0` forever. Nothing consumed
 * `maxDisplay` at the time, so it cost nothing and nobody noticed — until #4828
 * routed the Adsterra display unit through the guardrail and the network
 * silently served no anonymous visitor at all.
 *
 * Clamping is the honest reading of intent rather than a workaround: if the
 * shallow caps only permit two units, then seeing two units IS the strongest
 * engagement signal that profile can produce, so it is the right moment to
 * promote. Floor of 1 so a fully-closed profile (all caps 0, e.g. a hardened
 * `pro`-like tier) still yields a sane threshold instead of promoting at 0.
 */
export function deepDepthThresholdFor(shallow: AggressionProfile): number {
	const reachable = Math.min(
		shallow.maxDisplay + shallow.maxAffiliate,
		shallow.sessionImpressionCap,
	);
	return Math.max(1, Math.min(DEEP_DEPTH_THRESHOLD, reachable));
}

export function resolveSegment(input: SegmentInput): Segment {
	if (input.tier === "pro") return "pro";
	const threshold = input.shallowProfile
		? deepDepthThresholdFor(input.shallowProfile)
		: DEEP_DEPTH_THRESHOLD;
	const deep = input.returning === true || input.sessionDepth >= threshold;
	if (input.tier === "anon") return deep ? "anon_deep" : "anon_shallow";
	return deep ? "free_deep" : "free_shallow";
}

/** The shallow segment a non-pro tier starts in. */
export function shallowSegmentFor(tier: Exclude<Tier, "pro">): Segment {
	return tier === "anon" ? "anon_shallow" : "free_shallow";
}

export function aggressionFor(
	settings: MonetizationSettings,
	segment: Segment,
): AggressionProfile {
	return settings.aggression[segment];
}
